/**
 * DashboardRoleView - Composants personnalisés par rôle
 * 
 * RÈGLES DE VISIBILITÉ:
 * - Admin Full: Vue globale (toutes équipes, tous utilisateurs, stats complètes)
 * - Admin: Vue globale simplifiée
 * - VP Superviseur: Ses équipes + ses VP + apporteurs de l'équipe
 * - VP Payeur: Son équipe + ses apporteurs + ses stats
 * - Apporteur: Ses propres stats uniquement
 * - Comptable: Vue financière
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
    Users, Crown, Target, TrendingUp, Award, Star, 
    Briefcase, Euro, Calendar, Clock, CheckCircle2,
    ArrowRight, Zap, HardHat, Trophy, Medal, Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

// Formater les montants
const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value || 0);
};

// Stages gagnés (alignés sur reporting_service.py)
const VALIDATED_EP_STAGES = ['ep_gagne', 'ep_contrat_signe', 'ep_questionnaire_chantier'];
const VALIDATED_CH_STAGES = ['ch_cloture', 'ch_valide', 'ch_paiement_effectue'];

// Helper: filtrer les prospects "gagnés" selon la période sélectionnée
const filterWonByPeriod = (prospects, advancedFilters) => {
    const periodStart = advancedFilters?.dateFrom || null;
    const periodEnd = advancedFilters?.dateTo || null;
    const hasPeriodFilter = !!(periodStart && periodEnd);
    
    return prospects.filter(p => {
        const isWonEP = VALIDATED_EP_STAGES.includes(p.easy_power_stage);
        const isWonCH = VALIDATED_CH_STAGES.includes(p.chantier_stage);
        if (!isWonEP && !isWonCH) return false;
        if (!hasPeriodFilter) return true;
        
        const inPeriod = (dateStr) => {
            if (!dateStr) return false;
            const d = new Date(dateStr);
            return d >= periodStart && d <= periodEnd;
        };
        
        return (isWonEP && inPeriod(p.easy_power_stage_changed_at)) || 
               (isWonCH && inPeriod(p.chantier_stage_changed_at));
    });
};

// Helper: label de la période
const getPeriodLabel = (advancedFilters) => {
    const type = advancedFilters?.periodType;
    if (!type || type === 'all') return 'toutes périodes';
    if (type === 'month') return 'ce mois';
    if (type === 'week') return 'cette semaine';
    if (type === 'quarter') return 'ce trimestre';
    if (type === 'year') return 'cette année';
    if (type === 'custom') return 'période personnalisée';
    return 'toutes périodes';
};

// ============================================================
// VUE APPORTEUR - Mes performances personnelles
// ============================================================
export function ApporteurDashboardView({ user, prospects, commissions, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Stats personnelles
    const myProspects = prospects.filter(p => p.assigned_to === user?.id || p.dossier_apporteur_id === user?.id);
    const myWonProspects = filterWonByPeriod(myProspects, advancedFilters);
    const myInProgress = myProspects.filter(p => {
        const isWon = VALIDATED_EP_STAGES.includes(p.easy_power_stage) || VALIDATED_CH_STAGES.includes(p.chantier_stage);
        return !isWon && (p.easy_power_stage || p.chantier_stage);
    });
    
    // Calculer mes commissions
    const myTotalCommission = myWonProspects.reduce((sum, p) => 
        sum + (p.ep_commission_apporteur || 0) + (p.ch_commission_apporteur || 0), 0
    );
    
    // Taux de conversion
    const conversionRate = myProspects.length > 0 
        ? Math.round((myWonProspects.length / myProspects.length) * 100) 
        : 0;

    // Clic sur un KPI -> afficher EXACTEMENT la liste des dossiers qui composent le cadre
    const [selectedKpi, setSelectedKpi] = useState(null);
    const listRef = useRef(null);
    const apporteurCommission = (p) => (p.ep_commission_apporteur_1 || p.ep_commission_apporteur || 0) + (p.ch_commission_apporteur_1 || p.ch_commission_apporteur || 0);
    const kpiConfig = {
        prospects: { label: 'Mes prospects', items: myProspects, showCommission: false },
        in_progress: { label: 'En cours', items: myInProgress, showCommission: false },
        won: { label: 'Gagnés', items: myWonProspects, showCommission: true },
        commission: { label: 'Ma commission', items: myWonProspects, showCommission: true },
    };
    const toggleKpi = (k) => {
        setSelectedKpi((cur) => (cur === k ? null : k));
        setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    };
    const selected = selectedKpi ? kpiConfig[selectedKpi] : null;

    return (
        <div className="space-y-6" data-testid="apporteur-dashboard">
            {/* Message de bienvenue personnalisé */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-green-100">
                            <Trophy className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-green-900">
                                Mes Performances
                            </h2>
                            <p className="text-green-700">
                                Suivez vos résultats et votre progression
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats principales — cliquables pour voir les dossiers concernés */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-prospects"
                    onClick={() => toggleKpi('prospects')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5', selectedKpi === 'prospects' && 'ring-2 ring-blue-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Mes prospects</p>
                                <p className="text-3xl font-bold text-slate-900">{myProspects.length}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-in-progress"
                    onClick={() => toggleKpi('in_progress')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5', selectedKpi === 'in_progress' && 'ring-2 ring-amber-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">En cours</p>
                                <p className="text-3xl font-bold text-amber-600">{myInProgress.length}</p>
                            </div>
                            <Clock className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-won"
                    onClick={() => toggleKpi('won')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-green-200 bg-green-50/30', selectedKpi === 'won' && 'ring-2 ring-green-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{myWonProspects.length}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card role="button" tabIndex={0} data-testid="apporteur-kpi-commission"
                    onClick={() => toggleKpi('commission')}
                    className={cn('cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-emerald-200 bg-emerald-50/30', selectedKpi === 'commission' && 'ring-2 ring-emerald-400')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-emerald-600">Ma commission</p>
                                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(myTotalCommission)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-emerald-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Liste des dossiers qui composent le cadre sélectionné */}
            {selected && (
                <Card ref={listRef} data-testid="apporteur-kpi-list" className="border-slate-200">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Activity className="w-5 h-5 text-slate-600" />
                                {selected.label} — {selected.items.length} dossier{selected.items.length > 1 ? 's' : ''}
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedKpi(null)} data-testid="apporteur-kpi-list-close">Fermer</Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {selected.items.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">Aucun dossier dans cette catégorie.</p>
                        ) : (
                            <div className="divide-y">
                                {selected.items.map((p) => (
                                    <div key={p.id}
                                        className="flex items-center justify-between gap-3 py-3 cursor-pointer hover:bg-slate-50 -mx-4 px-4"
                                        onClick={() => navigate(`/prospects/${p.id}`)}
                                        data-testid={`apporteur-list-row-${p.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-medium truncate">{p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}</p>
                                            <p className="text-sm text-slate-500 truncate">{p.first_name} {p.last_name}</p>
                                        </div>
                                        {selected.showCommission ? (
                                            <span className="text-sm font-bold text-emerald-700 shrink-0">{formatCurrency(apporteurCommission(p))}</span>
                                        ) : (
                                            <Badge variant="outline" className="shrink-0">{p.easy_power_stage || p.chantier_stage || 'En cours'}</Badge>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* Taux de conversion */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-600" />
                        Mon taux de conversion
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Prospects → Gagnés</span>
                            <span className="text-lg font-bold text-blue-600">{conversionRate}%</span>
                        </div>
                        <Progress value={conversionRate} className="h-3" />
                        <p className="text-xs text-slate-500">
                            {myWonProspects.length} dossiers gagnés sur {myProspects.length} prospects
                        </p>
                    </div>
                </CardContent>
            </Card>
            
            {/* Mes derniers prospects */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Activity className="w-5 h-5 text-slate-600" />
                            Mes derniers prospects
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/prospects')}>
                            Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {myProspects.slice(0, 5).map(prospect => (
                        <div 
                            key={prospect.id}
                            className="flex items-center justify-between py-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 -mx-4 px-4"
                            onClick={() => navigate(`/prospects/${prospect.id}`)}
                        >
                            <div>
                                <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
                                <p className="text-sm text-slate-500">{prospect.company_name || 'Particulier'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {prospect.easy_power_stage === 'ep_gagne' && (
                                    <Badge className="bg-green-100 text-green-700">Gagné EP</Badge>
                                )}
                                {prospect.chantier_stage === 'ch_valide' && (
                                    <Badge className="bg-blue-100 text-blue-700">Gagné CH</Badge>
                                )}
                                {!prospect.easy_power_stage?.includes('gagne') && !prospect.chantier_stage?.includes('valide') && (
                                    <Badge variant="outline">En cours</Badge>
                                )}
                            </div>
                        </div>
                    ))}
                    {myProspects.length === 0 && (
                        <p className="text-center text-slate-500 py-8">
                            Aucun prospect pour le moment
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE VP PAYEUR - Mon équipe et mes apporteurs
// ============================================================
export function VPPayeurDashboardView({ user, prospects, teamMembers, teams, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Mes apporteurs
    const myApporteurs = teamMembers.filter(m => m.role === 'apporteur');
    
    // Stats de mon équipe
    const teamProspects = prospects.filter(p => {
        const teamIds = [user?.id, ...myApporteurs.map(a => a.id)];
        return teamIds.includes(p.assigned_to) || teamIds.includes(p.dossier_vp_id) || teamIds.includes(p.dossier_apporteur_id);
    });
    
    const teamWon = filterWonByPeriod(teamProspects, advancedFilters);
    
    // Ma commission totale
    const myCommissionBrute = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_vp_payeur_brute || 0) + (p.ch_commission_vp || 0), 0
    );
    const myCommissionNette = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_vp_payeur_nette || 0) + (p.ch_commission_vp || 0), 0
    );
    
    // Restant à payer aux apporteurs
    const restantApporteurs = teamWon.reduce((sum, p) => 
        sum + (p.ep_commission_apporteur || 0) + (p.ch_commission_apporteur || 0), 0
    );
    
    // Performance par apporteur
    const apporteurStats = myApporteurs.map(apporteur => {
        const apporteurProspects = teamProspects.filter(p => 
            p.dossier_apporteur_id === apporteur.id || p.assigned_to === apporteur.id
        );
        const won = filterWonByPeriod(apporteurProspects, advancedFilters);
        return {
            ...apporteur,
            prospects: apporteurProspects.length,
            won: won.length,
            commission: won.reduce((sum, p) => sum + (p.ep_commission_apporteur || 0), 0)
        };
    }).sort((a, b) => b.won - a.won);
    
    return (
        <div className="space-y-6" data-testid="vp-payeur-dashboard">
            {/* Message de bienvenue */}
            <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-purple-100">
                            <Crown className="w-8 h-8 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-purple-900">
                                Mon Équipe
                            </h2>
                            <p className="text-purple-700">
                                {myApporteurs.length} apporteur{myApporteurs.length > 1 ? 's' : ''} dans votre équipe
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats équipe */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Dossiers équipe</p>
                                <p className="text-3xl font-bold text-slate-900">{teamProspects.length}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{teamWon.length}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-purple-200 bg-purple-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-purple-600">Ma commission (nette)</p>
                                <p className="text-2xl font-bold text-purple-700">{formatCurrency(myCommissionNette)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-purple-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Commission Apporteurs</p>
                                <p className="text-2xl font-bold text-amber-700">{formatCurrency(restantApporteurs)}</p>
                            </div>
                            <Users className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Performance de mes apporteurs */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-600" />
                            Mes Apporteurs
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/teams')}>
                            Gérer <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {apporteurStats.map((apporteur, index) => (
                            <div 
                                key={apporteur.id}
                                className="flex items-center justify-between py-3 border-b last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-medium",
                                        index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-700" : "bg-slate-300"
                                    )}>
                                        {index < 3 ? <Medal className="w-4 h-4" /> : index + 1}
                                    </div>
                                    <div>
                                        <p className="font-medium">{apporteur.first_name} {apporteur.last_name}</p>
                                        <p className="text-xs text-slate-500">
                                            {apporteur.prospects} prospect{apporteur.prospects > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-green-600">{apporteur.won} gagné{apporteur.won > 1 ? 's' : ''}</p>
                                    <p className="text-xs text-slate-500">{formatCurrency(apporteur.commission)}</p>
                                </div>
                            </div>
                        ))}
                        {myApporteurs.length === 0 && (
                            <p className="text-center text-slate-500 py-8">
                                Aucun apporteur dans votre équipe
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE VP SUPERVISEUR - Toutes mes équipes et VP
// ============================================================
export function VPSuperviseurDashboardView({ user, prospects, teamMembers, teams, allUsers, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Mes équipes (où je suis superviseur)
    const myTeams = teams.filter(t => t.supervisor_vp_id === user?.id);
    
    // Mes VP Payeurs
    const myVPs = allUsers?.filter(u => 
        u.role === 'vp' && myTeams.some(t => t.payer_vp_id === u.id)
    ) || [];
    
    // Tous les membres de mes équipes
    const allTeamMemberIds = myTeams.flatMap(t => t.members?.map(m => m.id) || []);
    allTeamMemberIds.push(user?.id);
    myVPs.forEach(vp => allTeamMemberIds.push(vp.id));
    
    // Prospects de toutes mes équipes
    const allTeamProspects = prospects.filter(p => 
        allTeamMemberIds.includes(p.assigned_to) || 
        allTeamMemberIds.includes(p.dossier_vp_id) ||
        allTeamMemberIds.includes(p.dossier_apporteur_id)
    );
    
    const totalWon = filterWonByPeriod(allTeamProspects, advancedFilters);
    
    // Ma commission superviseur
    const myCommission = totalWon.reduce((sum, p) => 
        sum + (p.ep_commission_vp_superviseur || 0), 0
    );
    
    // Stats par équipe
    const teamStats = myTeams.map(team => {
        const teamMemberIds = [team.payer_vp_id, ...(team.members?.map(m => m.id) || [])];
        const teamProspects = allTeamProspects.filter(p => 
            teamMemberIds.includes(p.assigned_to) || teamMemberIds.includes(p.dossier_vp_id)
        );
        const won = filterWonByPeriod(teamProspects, advancedFilters);
        return {
            ...team,
            prospects: teamProspects.length,
            won: won.length,
            commission: won.reduce((sum, p) => sum + (p.ep_commission_vp_superviseur || 0), 0)
        };
    }).sort((a, b) => b.won - a.won);
    
    return (
        <div className="space-y-6" data-testid="vp-superviseur-dashboard">
            {/* Message de bienvenue */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-full bg-amber-100">
                            <Star className="w-8 h-8 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-amber-900">
                                Vue Superviseur
                            </h2>
                            <p className="text-amber-700">
                                {myTeams.length} équipe{myTeams.length > 1 ? 's' : ''} • {myVPs.length} VP
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Stats globales */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Mes équipes</p>
                                <p className="text-3xl font-bold text-slate-900">{myTeams.length}</p>
                            </div>
                            <Users className="w-10 h-10 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Total prospects</p>
                                <p className="text-3xl font-bold text-slate-900">{allTeamProspects.length}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-slate-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{totalWon.length}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Ma commission</p>
                                <p className="text-2xl font-bold text-amber-700">{formatCurrency(myCommission)}</p>
                            </div>
                            <Euro className="w-10 h-10 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Performance par équipe */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-600" />
                            Performance par Équipe
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/teams')}>
                            Gérer <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {teamStats.map((team, index) => (
                            <div 
                                key={team.id}
                                className="p-4 rounded-lg border bg-slate-50/50"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                                            index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : "bg-slate-300"
                                        )}>
                                            {index + 1}
                                        </div>
                                        <div>
                                            <p className="font-semibold">{team.name}</p>
                                            <p className="text-xs text-slate-500">
                                                VP: {team.payer_vp_name || 'Non assigné'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-green-600">{team.won} gagné{team.won > 1 ? 's' : ''}</p>
                                        <p className="text-xs text-slate-500">{team.prospects} prospects</p>
                                    </div>
                                </div>
                                <Progress 
                                    value={team.prospects > 0 ? (team.won / team.prospects) * 100 : 0} 
                                    className="h-2" 
                                />
                            </div>
                        ))}
                        {myTeams.length === 0 && (
                            <p className="text-center text-slate-500 py-8">
                                Aucune équipe sous votre supervision
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ============================================================
// VUE ADMIN - Vue globale complète
// ============================================================
export function AdminDashboardView({ prospects, allUsers, teams, commissions, dashboardStats, advancedFilters }) {
    const navigate = useNavigate();
    const periodLabel = getPeriodLabel(advancedFilters);
    
    // Utiliser les stats pré-calculées du Dashboard (filtrées par période)
    const totalProspects = dashboardStats?.totalProspects ?? prospects.length;
    const wonProspects = dashboardStats?.prospectsGagnes || filterWonByPeriod(prospects, advancedFilters);
    const validatedCount = dashboardStats?.validatedCount ?? wonProspects.length;
    
    // Stats par rôle
    const vpCount = allUsers?.filter(u => u.role === 'vp' && u.is_active).length || 0;
    const apporteurCount = allUsers?.filter(u => u.role === 'apporteur' && u.is_active).length || 0;
    
    // Commission Valnexo sur les dossiers gagnés (filtrés par période)
    const totalValnexoCommission = dashboardStats?.commissionsValidees ?? wonProspects.reduce((sum, p) => 
        sum + (p.ep_commission_valnexo || 0) + (p.ch_commission_valnexo || 0), 0
    );
    
    // Top performers - filtré par la même période
    const vpPerformance = allUsers?.filter(u => u.role === 'vp').map(vp => {
        const vpProspects = prospects.filter(p => p.dossier_vp_id === vp.id);
        const won = wonProspects.filter(p => p.dossier_vp_id === vp.id);
        return {
            ...vp,
            prospects: vpProspects.length,
            won: won.length
        };
    }).sort((a, b) => b.won - a.won).slice(0, 5) || [];
    
    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            {/* Stats globales */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Total dossiers</p>
                                <p className="text-3xl font-bold text-slate-900">{totalProspects}</p>
                                <p className="text-xs text-slate-400 mt-1">Dossiers {periodLabel}</p>
                            </div>
                            <Briefcase className="w-8 h-8 text-blue-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-green-200 bg-green-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600">Dossiers gagnés</p>
                                <p className="text-3xl font-bold text-green-700">{validatedCount}</p>
                                <p className="text-xs text-green-500 mt-1">Gagnés {periodLabel}</p>
                            </div>
                            <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">VP actifs</p>
                                <p className="text-3xl font-bold text-purple-600">{vpCount}</p>
                            </div>
                            <Crown className="w-8 h-8 text-purple-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Apporteurs actifs</p>
                                <p className="text-3xl font-bold text-emerald-600">{apporteurCount}</p>
                            </div>
                            <Users className="w-8 h-8 text-emerald-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600">Commission Valnexo</p>
                                <p className="text-xl font-bold text-amber-700">{formatCurrency(totalValnexoCommission)}</p>
                                <p className="text-xs text-amber-500 mt-1">Commission {periodLabel}</p>
                            </div>
                            <Euro className="w-8 h-8 text-amber-500 opacity-50" />
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Top VP */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-600" />
                            Top VP
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
                            Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {vpPerformance.map((vp, index) => (
                            <div 
                                key={vp.id}
                                className="flex items-center justify-between py-2 border-b last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                                        index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : index === 2 ? "bg-amber-700" : "bg-slate-300"
                                    )}>
                                        {index < 3 ? <Medal className="w-4 h-4" /> : index + 1}
                                    </div>
                                    <div>
                                        <p className="font-medium">{vp.first_name} {vp.last_name}</p>
                                        <Badge className="text-xs bg-purple-100 text-purple-700">VP</Badge>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-green-600">{vp.won} gagné{vp.won > 1 ? 's' : ''}</p>
                                    <p className="text-xs text-slate-500">{vp.prospects} prospects</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default {
    ApporteurDashboardView,
    VPPayeurDashboardView,
    VPSuperviseurDashboardView,
    AdminDashboardView
};
